import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Buttom from "../../../components/Button/Button";
import CustomDropdown from "../../../components/CustomDropdown/CustomDropdown";
import { useMessages } from "../../../contexts/MessageContext";
import { SettingsContext } from "../../../contexts/SettingsContext";
import useFileToBase64 from "../../../hooks/useFileToBase64";
import useImageCompression from "../../../hooks/useImageCompress";
import useIsMobile from "../../../hooks/useIsMobile.jsx";
import { sendImageFile, sendImageOffice, sendImageTruck } from "../../../utils/ApiBot";
import ServerResponse from "../ServerResponse/ServerResponse";
import "./Camera.css";

const Camera = () => {
  const [selectedCamera, setSelectedCamera] = useState("");
  const [analysisType, setAnalysisType] = useState(() => {
    const stored = localStorage.getItem("analysisType");
    return stored || "";
  });
  const [isSending, setIsSending] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isMobile = useIsMobile();

  const videoRef = useRef(null);
  const [phoneStream, setPhoneStream] = useState(null);
  const {addMessage, updateMessage} = useMessages();
  const {compressImage} = useImageCompression();
  const {dataURLtoFile} = useFileToBase64();

  const {cameras, selectedCameras} = useContext(SettingsContext);

  const availableCameras = cameras.filter((camera) => selectedCameras[camera.id]);
  const currentCamera = availableCameras.find((cam) => cam.id === selectedCamera);

  const handleAnalysisTypeChange = (val) => {
    setAnalysisType(val);
    localStorage.setItem("analysisType", val);
  };

  const analysisOptions = [
    {label: "Обычный", value: "default"},
    {label: "Грузовики", value: "truck"},
    {label: "Офис", value: "office"},
  ];

  useEffect(() => {
    const stored = localStorage.getItem("analysisType");
    if (!stored) {
      localStorage.setItem("analysisType", "default");
      setAnalysisType("default");
    } else {
      setAnalysisType(stored);
    }
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  /* fix: смена камер */

  useEffect(() => {
    if (availableCameras.length && !selectedCamera) {
      setSelectedCamera(availableCameras[0].id);
    }
  }, [availableCameras, selectedCamera]);

  useEffect(() => {
    if (!currentCamera) return;
    if (currentCamera.id === "phone") {
      navigator.mediaDevices
        .getUserMedia({video: {facingMode: "environment"}})
        .then((stream) => {
          setPhoneStream(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error("Ошибка доступа к камере телефона:", err);
        });
      if (phoneStream) {
        phoneStream.getTracks().forEach((track) => track.stop());
        setPhoneStream(null);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = currentCamera.src;
      }
    }
    return () => {
      if (phoneStream) {
        phoneStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [selectedCamera, currentCamera]);

  /*   const handleScreenshot = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/png');
      if (addMessage) {
        addMessage({type: 'image', content: imageData});
      }
    }
  }; */

  const handleScreenshot = async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("Подключите камеру");
      return;
    }
    setIsSending(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);

      const dataUrl = canvas.toDataURL("image/png");

      if (!dataUrl.startsWith("data:image")) {
        toast.error("Не удалось создать изображение");
        return;
      }

      let screenshotFile;
      try {
        screenshotFile = dataURLtoFile(dataUrl, `screenshot-${Date.now()}.png`);
      } catch (err) {
        console.error(err);
        toast.error("Ошибка при конвертации изображения");
        return;
      }

      const compressed = await compressImage(screenshotFile);
      const formData = new FormData();
      formData.append("file", compressed, compressed.name);

      const loaderId = Date.now();
      const previewUrl = URL.createObjectURL(compressed);
      /* addMessage({type: "image", content: previewUrl}); */
      addMessage({type: "loader", id: loaderId, timestamp: loaderId});

      /* const apiResponse = await sendImageFile(formData); */
      /* const apiResponse = await sendImageTruck(formData); */
      let apiResponse;
      if (analysisType === "default") {
        apiResponse = await sendImageFile(formData);
      } else if (analysisType === "truck") {
        apiResponse = await sendImageTruck(formData);
      } else if (analysisType === "office") {
        apiResponse = await sendImageOffice(formData);
      }

      updateMessage(loaderId, {
        type: "custom",
        content: <ServerResponse rawResponse={apiResponse.openai_response} />,
      }); /* fix: поправить ответ сервера */

      if (isMobile) {
        setIsCollapsed(true);
      }
      /* console.log(apiResponse); */
      toast.success("Отчет успешно отправлен");
    } catch (err) {
      toast.error("Ошибка отправки отчета");
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className='camera-content'>
      <div className='container'>
        <CustomDropdown
          options={availableCameras.map((cam) => ({label: cam.name, value: cam.id}))}
          selectedValue={selectedCamera}
          onChange={(val) => setSelectedCamera(val)}
          type='camera'
        />
        <CustomDropdown options={analysisOptions} selectedValue={analysisType} onChange={handleAnalysisTypeChange} type='analysis' />
      </div>
      {isMobile && (
        <div className='toggle-container'>
          <span>Камера</span>
          <label className='switch'>
            <input type='checkbox' checked={!isCollapsed} onChange={() => setIsCollapsed((prev) => !prev)} />
            <span className='slider' />
          </label>
        </div>
      )}

      <div className={`camera-video ${isCollapsed ? "collapsed" : ""}`}>
        {currentCamera ? (
          <video ref={videoRef} autoPlay muted playsInline webkit-playsinline='true' style={{width: "100%"}} />
        ) : (
          <p>Нет доступных камер</p>
        )}
      </div>

      <Buttom
        buttonId='screenshot__button'
        buttonName='Контроль уборки'
        additionalClass='screenshot__button'
        onClick={handleScreenshot}
        disabled={!analysisType || isSending}
      >
        Скриншот
      </Buttom>
    </div>
  );
};

export default Camera;
